import type { WorkSheet } from 'xlsx'
import { utils, writeFile } from 'xlsx'

export type PreventCellToNum = (params: { row: number, col: number, value: string }) => boolean

/**
 * 格式化单元格中的字符串为数字格式
 * @param worksheet 
 * @param fmt 
 */
export const formatNumberCol = (worksheet: WorkSheet, preventCellToNum?: PreventCellToNum) => {
  /**
   * range.s.r 就是 range.start.row 行开始，从 0 开始
   * range.e.r 就是 range.end.row   行结尾
   * range.s.c 就是 range.start.col 列开始，从 0 开始
   * range.e.c 就是 range.end.col   列结尾
   * 
   * 在合并单元格中，不管是行合并还是列合并，row 和 col 都从 0 开始，并且会按照非合并单元格的行和列数进行循环，
   * 当 row 和 col 对应到合并单元格时，只有首次对应的值为合并单元格的值，其余的对应都为 undefined
   */
  const range = utils.decode_range(worksheet['!ref']!)
  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const ref = utils.encode_cell({ r: row, c: col })
      /**
       * const { v, t, z } = worksheet[ref]
       * v: raw value (number, string, Date object, boolean)
       * t: type: b Boolean, e Error, n Number, d Date, s Text, z Stub
       * z: number format string associated with the cell (if requested)
       */
      const { v, t } = worksheet[ref] ?? {}
      const isNum = t !== 'n' && typeof v === 'string' && (v.trim() !== '') && !isNaN(Number(v))
      if (isNum && !preventCellToNum?.({ row, col, value: v })) {
        worksheet[ref].t = 'n'
      }
    }
  }
}

/**
 * 将多个表格导出为 execl
 * @param doms table 的 dom 节点，支持多个 dom
 * @param fileName 导出的文件名称
 */
export const tableExportToBook = (
  doms: Element[],
  fileName: string = '表单列表',
  // 有些cell内容是手机号、或者编号0001之类的，不需要转为数字
  preventCellToNum?: PreventCellToNum
) => {
  if (!Array.isArray(doms) || !doms.length) return

  let ws: WorkSheet | undefined = undefined
  doms.forEach((dom: any) => {
    /**
     * { raw: true } 参数表示保留原始字符串
     * 1、避免将 Date 字符串进行转换，这里就会有时区问题，见：https://github.com/SheetJS/sheetjs/issues/1470
     * 2、避免不必要的数字转换，比如将 18064505555 转换成 1.84E+10
     * ps：如果出现时区问题，可用 dayjs(date).format() 来解决
     */
    if (!ws) {
      ws = utils.table_to_sheet(dom, { raw: true })
    } else {
      utils.sheet_add_dom(ws, dom, { raw: true, origin: -1 })
    }
  })
  // 格式化单元格中的数字
  formatNumberCol(ws!, preventCellToNum)
  // 新建工作簿
  const wb = utils.book_new()
  // 将 sheet 添加到工作簿中
  utils.book_append_sheet(wb, ws!, 'Sheet1')
  writeFile(wb, `${fileName}.xlsx`)
}